<template>
    <div id="diagram">
      <svg @click="selectElement($event)" ref="svg" id="svg">
        
        <defs>
          <filter id="shadow">
            <feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="hsl(219, 13%, 50%)" />
          </filter>
          <filter id="shadow2">
            <feDropShadow dx="0" dy="0" stdDeviation=".5" flood-color="hsl(219, 13%, 50%)" />
          </filter>
          <filter id="shadow3">
            <feDropShadow dx="0" dy="3" stdDeviation="4" flood-color="hsl(219, 13%, 50%)" />
          </filter>
        </defs>

        <g @mousedown="selectElement($event)" v-for="node in nodes" :key="node.id" :id="node.id" class="node">
          <foreignObject :x="node.x" :y="node.y" :height="node.height" :width="node.width">
            <div class="node-container" :class="node.className" xmlns="http://www.w3.org/1999/xhtml">
              <div class="node-content">
                <div class="node-label">
                  <icon :name="node.icon.name" :color="node.icon.color" />
                  <span class="node-label-text">{{ node.label }}</span>
                </div>

                <div v-if="node.attachment.hasOwnProperty('comment') && node.attachment.comment.length > 0"
                  class="node-comment" >
                  {{ node.attachment.comment }}
                </div>

                <div v-if="node.attachment.hasOwnProperty('innerImage') && node.attachment.innerImage" class="node-inner-img">
                  <img :src="node.attachment.innerImage" :width="node.attachment.imgWidth" :height="node.attachment.imgHeight" alt="">
                </div>

                <div v-for="outcome in node.outcomes" :key="outcome.id" class="outcome">
                  <span class="outcome-text">{{ outcome.text }}</span>
                  <span :id="'outcome-' + outcome.id"></span>
                </div>
              </div>
            </div>
          </foreignObject>
          
          <g v-if="hasMounted"> 
            <circle v-for="outcome in node.outcomes" :key="outcome.id"
              :cx="node.x + node.width" :cy="getYPosition('outcome-' + outcome.id)"
              r="6" :id="'connector-' + outcome.id" :data-node="node.id" :data-label="outcome.text" data-connector-type="outcome-connector"
              :data-expression="outcome.expression" class="connector" />
            <rect :x="node.x" :y="node.y" :width="node.width" :height="node.height"
              stroke="transparent" stroke-width="2" fill="transparent" class="node-frame" :data-node-id="node.id" />
            <foreignObject v-if="node.attachment.hasOwnProperty('image') && node.attachment.image" :x="node.x - node.attachment.imgWidth/2 + node.width/2"
              :y="node.y + node.height + 25" :width="node.attachment.imgWidth" :height="node.attachment.imgHeight">
              <g class="user-card" :data-node-id="node.id">
                <div class="user-pic">
                  <img :src="node.attachment.image" width="100%" height="100%" alt="user pic" />
                </div>
              </g>
            </foreignObject>
          </g>

          <circle :cx="node.x + node.width/2" :cy="node.y" r="6" class="connector" :id="`${node.id}-top`" :data-label="node.label"
            :data-node="node.id" data-position="top" data-connector-type="node-connector" />
          <circle v-if="node.className !== 'user-task' && node.className !== 'decision'" 
            :cx="node.x + node.width" :cy="node.y + node.height/2" r="6" class="connector" :data-label="node.label"
            :id="`${node.id}-right`" :data-node="node.id" data-position="right" data-connector-type="node-connector" />
          <circle :cx="node.x + node.width/2" :cy="node.y + node.height" r="6" class="connector" :data-label="node.label"
            :id="`${node.id}-bottom`" :data-node="node.id" data-position="bottom" data-connector-type="node-connector" />
          <circle :cx="node.x" :cy="node.y + node.height/2" r="6" class="connector" :id="`${node.id}-left`" :data-label="node.label"
            :data-node="node.id" data-position="left" data-connector-type="node-connector" />
        </g>
        <circle @click.prevent id="floating-point" cx="0" cy="0" r="3" 
          style="visibility: hidden; pointer-events: visiblePainted" />
        <rect id="drag-node" x="0" y="0" rx="5" height="48" width="110" class="drag-node" />
      </svg>
    </div>
</template>

<script>
  import db from '@/services/db'
  import { mapState, mapMutations } from 'vuex'
  import Vue from 'vue';
  import PlainDraggable from '@/libs/plain-draggable/plain-draggable.min'
  import LeaderLine from '@/libs/leader-line/leader-line.min'
  import { calcBBox, getTextBoxSize } from '../utils/nodes'
  import { makeDraggable } from '../utils/draggable'

  export default {
    props: {
      leaderLines: { 
        type: Array, default: []
      }, 
    },
    data(){
      return {
        scale: 1,
        offsetX: 0,
        offsetY: 0,
        lineAdded: false,
        hasMounted: false,
        connectMode: false, // true when user is making a connection 
        /* options for Leader Line instances */
        connectionOptions: {
          regular:  {
            startPlug: 'behind',
            color: 'hsla(219, 79%, 66%, 0.6)',
            size: 3,
          },
          rejected: {
            color: 'hsla(348, 83%, 47%, 0.6)',
            startPlug: 'behind',
            endPlug: 'disc', size: 3
          },
          approved: {
            color: 'hsl(154, 64%, 49%)',
            startPlug: 'behind'
          },
          connectMode: {
            color: 'hsla(219, 79%, 66%, 0.6)',
            size: 3,
            endPlug: 'disc',
            dash: {animation: true}
          }
        }
      }
    },
    computed: {
      ...mapState('diagramBuilder', [
        'nodes',
        'currentNode',
        'lines',
        'currentElement',
        'workflowId',
        'metaData',
      ]),
    },
    methods: {
      ...mapMutations('diagramBuilder', [
        'setCurrentNode',
        'updateNode',
        'addLine',
        'updateCounter',
        'setCurrentElement'
      ]),
      getScale() {
        let scale 
        if (!window.devicePixelRatio || !navigator.userAgent.includes('Chrome')) {
          scale = 1
        } else {
          scale = window.devicePixelRatio
        }
        return scale
      },
      getOffsetX() {
        if (this.hasMounted) {
          let offsetX = this.$refs.svg.getBoundingClientRect().left 
          return offsetX
        } 
      },
      getOffsetY() {
        if (this.hasMounted) {
          let offsetY = this.$refs.svg.getBoundingClientRect().top 
          return offsetY
        }
      },
      getYPosition(id) {
        /* this methods is used for getting y coordinate of outcome connectors */
        let ghostConnector = document.getElementById(id)
        let y = ghostConnector.getBoundingClientRect().y / this.scale - this.getOffsetY()
        return y
      },
      adjustNodeSize(){
        document.querySelectorAll('g.node').forEach(g => {
          let currentNode = Object.assign({}, this.nodes.find(node => node.id === g.id))
          currentNode.height = calcBBox(g).height
          currentNode.width = calcBBox(g).width
          this.updateNode(currentNode)
        })
      },
      connectModeHandler() {
        /* Allow users connect nodes by dragging */
        let that = this
        let source, target, floatingPoint, line

        function updateFloatingPointPosition(event) {
          floatingPoint.setAttribute('cx', event.clientX - that.offsetX)
          floatingPoint.setAttribute('cy', event.clientY - that.offsetY)
          line.position()
        }

        if(that.hasMounted && that.$refs.svg !== undefined) {
          that.$refs.svg.addEventListener('mousemove', function(event) {
            if (that.connectMode) updateFloatingPointPosition(event)
          })
  
          that.$refs.svg.addEventListener('mouseup', function(event) {
            if (!that.connectMode) return 
  
            target = event.target.closest('g.node')
            // some checks may be unnecessary
            if (target && source && that.hasMounted && !that.lineAdded) {
              if (target.id !== source.dataset.node) {
                line.setOptions({end: target, dash: false, endPlug: 'arrow'})
                line.id = 'line-' + +new Date
                line.anchor = source.id
                
                let label, expression, targetLabel
                
                if (source.dataset.hasOwnProperty('connectorType')) {
                  label = source.dataset.label
                  targetLabel = that.nodes.find(node => node.id === target.id).label

                  if (source.dataset.expression) {
                    expression = source.dataset.expression
                    that.addLine({source: source.id, target: target.id, id: line.id, instance: line, label: label,
                                  targetLabel: targetLabel, expression: expression, type: that.connectionOptions.regular})
                  } else {
                    that.addLine({source: source.id, target: target.id, id: line.id, instance: line, label: label, 
                                  targetLabel: targetLabel, expression: expression, type: that.connectionOptions.regular})
                  } 
                } else {
                  console.warn(`Source connector ${source.id} should have "connectorType" property, connection not added`)
                }
                that.lineAdded = true
              }
              line.remove()
              that.updateCounter()
            } else {
              source.classList.remove('anchor-active')
              line.remove()
            }
            that.connectMode = false
          })
  
          that.$refs.svg.addEventListener('mousedown', function(event) {
            if (event.button === 2) return
  
            if (event.target.matches('circle.connector') && that.hasMounted) {
              that.lineAdded = false
              floatingPoint = document.getElementById('floating-point')
              source = event.target
              source.classList.add('anchor-active')
              line = new LeaderLine(source, floatingPoint, that.connectionOptions.connectMode)
              that.connectMode = true
            }
          })
        }
      },
      initConnections() {
        let that = this

        that.lines.forEach(line => {
          let source = document.getElementById(line.source),
          target = document.getElementById(line.target),
          /* `frame` element is created specifically to be used as `target` element for Leader Line's instances. 
          The reason is that it's a regular SVG rectangle. If we specify a foreignObject instead, it will produce bugs 
          in Chrome. */
          arr = Array.from(document.querySelectorAll('rect.node-frame')),
          frame = arr.find(frame => frame.dataset.nodeId === line.target)
          
          const instance = new LeaderLine(
            source,
            frame,
            line.type
          )

          instance.id = line.id
          instance.anchor = line.source
          that.$emit('add-leader-line', instance) 
          source.classList.add('anchor-active')
        })
      },
      nodesHoverable() {
        this.$refs.svg.querySelectorAll('g.node').forEach(node => {
          node.addEventListener('mouseenter', function() {
            node.querySelectorAll('circle.connector').forEach(circle => circle.classList.add('anchor-hovered'))
          }, false)
          node.addEventListener('mouseleave', function() {
            node.querySelectorAll('circle.connector').forEach(circle => circle.classList.remove('anchor-hovered'))
          }, false)
        })
      },
      makeDraggable() {
        let that = this
        
        this.scale = this.getScale()

        that.nodes.forEach(node => {
          node = document.querySelector(`g#${node.id}`)
          
          let draggable = new PlainDraggable(node, {
            onDragStart: function() {
              /* Prevent dragging when a connector is being clicked */
              if (event.target.matches('circle.connector')) {
                return false
              }
            },
            onMove: function() { that.leaderLines.forEach(line => line.position()) },
            onDragEnd: function() { 
              /* Update node's position */
              that.setCurrentNode(node.id)
              let currentNode = Object.assign({}, that.currentNode)

              if (!_.isEmpty(currentNode) && !event.target.matches('circle.connector') && 
                  !that.connectMode) {
                let el = document.querySelector(`g#${currentNode.id}`)
                currentNode.x = Math.floor(el.firstElementChild.getBoundingClientRect().x / that.scale - that.getOffsetX()) 
                currentNode.y = Math.floor(el.firstElementChild.getBoundingClientRect().y / that.scale - that.getOffsetY()) 
                that.updateNode(currentNode)
                that.updateCounter()
              }
            },
          })
        })
      },
      selectElement(event){
        let that = this

        if (event.button === 2) return

        if (event.target.matches('circle.connector')) {
          let anchor = {}
          let relatedLines = []

          that.lines.forEach(line => {
            if (line.source === event.target.id) {
              relatedLines.push(line)
            }
          })
          anchor['relatedLines'] = relatedLines
          anchor.id = event.target.id
          anchor.position = event.target.dataset.position
          that.setCurrentElement(anchor)

        } else if (event.target.closest('g.node')) {
          let selectedNode = that.nodes.find(node => node.id === event.target.closest('g.node').id)
          that.setCurrentElement(selectedNode)

        } else if (event.target.matches('svg#svg')) {
          that.setCurrentElement(event.target)
        }
      },
      getSelectedNode() {
        /* We make a check for 'clazz' prop to see if the currentElement is a node (this is bad, right) */
        if (this.currentElement && this.currentElement.hasOwnProperty('clazz')) {
          const el = this.$refs.svg.getElementById(this.currentElement.id)
          return el
        } else return 
      },
      render(){
        this.$emit('remove-all-leader-lines')

        /* 1. Add existing lines */
        this.initConnections()

        /* 2. Allow users adding lines */
        this.connectModeHandler()

        /* 3. Make nodes draggable and re-position lines accordingly on drag */
        this.makeDraggable()

        /* 4 */
        this.adjustNodeSize()

        /* 5. show related anchors on node hover */
        this.nodesHoverable()

        /* 6.  */
        const el = this.getSelectedNode() 
        if (el) {
          el.classList.add('node-selected')
        }
      },
      saveWorkflow() {
        const flowData = {
          metaData: this.metaData,
          nodes: this.nodes,
          edges: this.lines,
        }
        db.workflow.save(this.workflowId, flowData)
      },
    },
    mounted(){
      let that = this

      this.scale = this.getScale()
      this.hasMounted = true
      
      if (!this.offsetX) this.offsetX = this.getOffsetX()
      if (!this.offsetY) this.offsetY = this.getOffsetY()

      this.$nextTick(() => {
        this.$nextTick( () => {
          this.render()
          this.leaderLines.forEach(line => {
            line.position()
          })
        } )
      })
    }, 
    updated() {
      this.$nextTick(() => this.saveWorkflow())
    },
    beforeDestroy() {
      this.$emit('remove-all-leader-lines')
    }
  };
</script>

<style lang="scss">
$svg-bg: #dfe5ec;
$border-regular: hsla(219, 44%, 76%, 0.9);
$border-lighter: hsla(219, 55%, 89%, 0.6);

#svg {
  width: 100%;
  height: 100%;
  background: $svg-bg;
  box-sizing: border-box;
}

#diagram {
  position: relative;
  width: 100%;
  height: 100%;
}

.node{ 
  filter: url(#shadow);
}

.node-selected{ 
  filter: url(#shadow3);
}

.node-container{
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 100px;
  min-height: 40px;
  background: white; 
  height: 100%;
  border-left: 5px solid cornflowerblue;
  border-radius: 4px; 

  .node-content{
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-bottom: .5em;
    
    .node-label{
      font-family: Roboto;
      font-size: 15px; 
      font-weight: 500; 
      line-height: 1.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1em .75em .5em .75em;

      :first-child{
        min-width: 15px;
      }

      :last-child{
        margin-left: .4em;
      }
      
      .node-label-text{
        white-space:nowrap;
      }
    }

    .node-comment{
      font-family: Roboto;
      font-size: 14px; 
      font-weight: 300; 
      line-height: 1.4rem;
      padding: 0 .5em 1em .5em; 
      margin-top: -1em; 
      word-break: break-word;
      white-space: pre-line;
    }

    .node-inner-img{
      padding: 0 1rem;
      img{
        object-fit: contain;
      }
    }
  }
}

.user-card{
  .user-pic{
    background: white;
    width: 100%;
    height: 100%;
    padding: 1rem 1.5rem;
    border-radius: 4px; 
    img{
      object-fit: contain;
    }
  }
}

.connector{
  fill: white;
  stroke: transparent;
  filter: url(#shadow2);
  stroke-width: 1;
  cursor: default;
  display: none;
}

.anchor-active{
  display: block;
  stroke-width: 2;
  stroke: hsla(219, 79%, 66%, 0.6);
  fill: white;
}

.anchor-hovered{
  display: block;
  filter: url(#shadow);
}

.outcome{
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid $border-lighter;
  border-bottom: 1px solid $border-lighter;
  padding: .25em .75em;

  .outcome-text{
    font-size: 14px;
  }
}

.drag-node{
  visibility: hidden;
  pointer-events: visiblePainted;
  fill: white;
  stroke: transparent;
  stroke-width: 1;
  filter: url(#shadow);
}

.outcome-connector{
  fill: white;
  filter: url(#shadow2);
  stroke: transparent;
  stroke-width: 2;
  cursor: default;
  display: none;
}

.tooltip{
  stroke: transparent;
  fill: white;
  filter: url(#shadow);
}

.start-event{
  border-left: 5px solid hsl(154, 64%, 49%);
}

.mail-task{
  border-left: 5px solid hsl(220, 54%, 65%);
}

.end-event{
  border-left: 5px solid hsl(348, 91%, 59%);
}

.script-task{
  border-left: 5px solid hsl(218, 54%, 20%);
}

.update-record{
  border-left: 5px solid hsl(189, 87%, 50%);
}

.user-task{
  border-left: 5px solid hsl(235, 71%, 65%);
}

.decision{
  border-left: 5px solid hsl(11, 96%, 62%);
}

.comment{
  border-left: 5px solid hsl(188, 78%, 41%);
}
</style>
